"use client";

import {
  NOISE_FLOOR_PERCENTILE,
  ONSET_SEARCH_MAX_SECONDS,
  RMS_WINDOW_SECONDS,
  SUSTAIN_REQUIRED_SECONDS,
  VOCAL_BAND_HIGHPASS_HZ,
  VOCAL_BAND_LOWPASS_HZ,
  VOCAL_ONSET_THRESHOLD_MULTIPLIER,
} from "@/constants/audioQuality";
import { ExtractedAudioSegment } from "@/types/defs/audioQuality/extractedAudioSegment";
import { extractAudioSegment } from "@/utils/audio/extractAudioSegment";
import { extractHighestEnergySegment } from "@/utils/audio/quality/extractHighestEnergySegment";

const renderVocalBandFilteredBuffer = async (
  audioBuffer: AudioBuffer,
  maxSeconds: number
): Promise<AudioBuffer> => {
  const renderLength = Math.min(
    audioBuffer.length,
    Math.floor(maxSeconds * audioBuffer.sampleRate)
  );
  const offlineContext = new OfflineAudioContext({
    numberOfChannels: 1,
    length: renderLength,
    sampleRate: audioBuffer.sampleRate,
  });

  const source = offlineContext.createBufferSource();
  source.buffer = audioBuffer;

  const highpass = offlineContext.createBiquadFilter();
  highpass.type = "highpass";
  highpass.frequency.value = VOCAL_BAND_HIGHPASS_HZ;

  const lowpass = offlineContext.createBiquadFilter();
  lowpass.type = "lowpass";
  lowpass.frequency.value = VOCAL_BAND_LOWPASS_HZ;

  source.connect(highpass);
  highpass.connect(lowpass);
  lowpass.connect(offlineContext.destination);
  source.start();

  return offlineContext.startRendering();
};

const computeWindowedRms = (
  samples: Float32Array,
  samplesPerWindow: number
): Float32Array => {
  const numWindows = Math.floor(samples.length / samplesPerWindow);
  const rms = new Float32Array(numWindows);

  for (let w = 0; w < numWindows; w++) {
    const start = w * samplesPerWindow;
    let sumSquares = 0;
    for (let s = 0; s < samplesPerWindow; s++) {
      const sample = samples[start + s];
      sumSquares += sample * sample;
    }
    rms[w] = Math.sqrt(sumSquares / samplesPerWindow);
  }

  return rms;
};

const computePercentile = (
  values: Float32Array,
  percentile: number
): number => {
  if (values.length === 0) {
    return 0;
  }
  const sorted = Float32Array.from(values).sort();
  const index = Math.min(
    sorted.length - 1,
    Math.floor(sorted.length * percentile)
  );
  return sorted[index];
};

// Returns the index of the first window where RMS goes above `threshold` and stays above it for at least `sustainWindowsRequired` consecutive windows. Returns -1 if no such region exists.
const findVocalOnsetWindowIndex = (
  rms: Float32Array,
  threshold: number,
  sustainWindowsRequired: number
): number => {
  let sustainStart = -1;
  let consecutiveAbove = 0;

  for (let i = 0; i < rms.length; i++) {
    if (rms[i] > threshold) {
      if (sustainStart === -1) {
        sustainStart = i;
      }
      consecutiveAbove++;
      if (consecutiveAbove >= sustainWindowsRequired) {
        return sustainStart;
      }
    } else {
      sustainStart = -1;
      consecutiveAbove = 0;
    }
  }

  return -1;
};

// Finds where vocals first start in `audioBuffer` using a vocal-band-filtered energy heuristic, then returns a `duration`-second slice of the *original* (untouched) audioBuffer starting at that point. The bandpass filter is only used internally to score the onset — the returned audio is unmodified. Falls back to extractHighestEnergySegment when no clear onset is detected (very quiet audio, no sustained vocal section, etc.) so we never produce a worse result than the previous peak-based selection.
export const extractVocalOnsetSegment = async ({
  audioBuffer,
  duration,
}: {
  audioBuffer: AudioBuffer;
  duration: number;
}): Promise<ExtractedAudioSegment> => {
  if (audioBuffer.duration <= duration) {
    return {
      audioBuffer,
      startSeconds: 0,
      endSeconds: audioBuffer.duration,
    };
  }

  const filteredBuffer = await renderVocalBandFilteredBuffer(
    audioBuffer,
    ONSET_SEARCH_MAX_SECONDS
  );
  const samplesPerWindow = Math.floor(
    audioBuffer.sampleRate * RMS_WINDOW_SECONDS
  );
  const rms = computeWindowedRms(
    filteredBuffer.getChannelData(0),
    samplesPerWindow
  );

  const noiseFloor = computePercentile(rms, NOISE_FLOOR_PERCENTILE);
  const threshold = noiseFloor * VOCAL_ONSET_THRESHOLD_MULTIPLIER;
  const sustainWindowsRequired = Math.ceil(
    SUSTAIN_REQUIRED_SECONDS / RMS_WINDOW_SECONDS
  );

  const onsetWindowIndex = findVocalOnsetWindowIndex(
    rms,
    threshold,
    sustainWindowsRequired
  );

  if (onsetWindowIndex === -1) {
    return extractHighestEnergySegment({ audioBuffer, duration });
  }

  const onsetTimeSeconds = onsetWindowIndex * RMS_WINDOW_SECONDS;
  const endTimeSeconds = Math.min(
    audioBuffer.duration,
    onsetTimeSeconds + duration
  );

  return {
    audioBuffer: await extractAudioSegment({
      audioBuffer,
      startTimeSeconds: onsetTimeSeconds,
      endTimeSeconds,
    }),
    startSeconds: onsetTimeSeconds,
    endSeconds: endTimeSeconds,
  };
};
